// Page Artisans 5 Étoiles - Les meilleurs artisans notés 5/5
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, MapPin, CheckCircle, Building2, Phone, Award, Shield, ChevronRight } from 'lucide-react';
import UnifiedHeader from '../../../components/layout/UnifiedHeader';
import MainNavigation from '../../../components/layout/MainNavigation';
import { MOCK_ARTISANS } from './ArtisansPage';
import { useDemoMode } from '../../../components/demo/DemoModeProvider';

// Filtrer UNIQUEMENT les artisans avec 5.0 étoiles
const ARTISANS_5_ETOILES = MOCK_ARTISANS.filter(a => a.rating_average === 5.0);

// Composant ShopCard identique à ArtisansPage
function ShopCard({ shop, index }) {
  const isTest = shop.id?.startsWith('artisan-');
  const { requireAuth } = useDemoMode();
  
  const handlePhoneClick = (e) => {
    e.preventDefault();
    if (requireAuth('contacter cet artisan')) {
      window.location.href = `tel:${shop.phone}`;
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-2xl overflow-hidden group relative h-full flex flex-col"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
      whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0,0,0,0.12)' }}
    >
      {/* Cover Image */}
      <div className="h-36 relative overflow-hidden">
        {shop.cover_image ? (
          <img
            src={shop.cover_image}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#2F4EA2] to-[#1E40AF]" />
        )}
        
        {/* Badge 5 ÉTOILES */}
        <div className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-yellow-400 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-lg">
          <Star size={14} fill="white" />
          5 ÉTOILES
        </div>
        
        {/* Badge vérifié */}
        {shop.is_verified && (
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-3 right-3 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg"
          >
            <CheckCircle size={14} />
            Vérifié
          </motion.div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>

      {/* Logo / Photo de profil */}
      <div className="relative px-5 -mt-10">
        <div className="w-20 h-20 rounded-2xl bg-white shadow-xl border-4 border-white overflow-hidden">
          {shop.logo ? (
            <img src={shop.logo} alt={shop.shop_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <Building2 size={32} className="text-gray-400" />
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-5 pt-3 flex flex-col flex-grow">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-lg text-gray-900 group-hover:text-[#F97316] transition-colors">{shop.shop_name}</h3>
            </div>
            <p className="text-[#1E40AF] font-semibold text-sm mt-0.5">{shop.metier}</p>
          </div>
          
          {/* Rating - Toujours 5.0 */}
          <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1.5 rounded-xl">
            <Star size={16} className="text-amber-500 fill-amber-500" />
            <span className="font-bold text-gray-900">5.0</span>
            <span className="text-gray-500 text-sm">({shop.reviews_count})</span>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-gray-500 mt-3">
          <MapPin size={15} />
          <span className="text-sm font-medium">{shop.city} ({shop.postal_code})</span>
        </div>

        {/* Description */}
        {shop.description && (
          <p className="text-gray-600 text-sm mt-3 line-clamp-2 leading-relaxed">{shop.description}</p>
        )}

        {/* Services Tags */}
        {shop.services && shop.services.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {shop.services.slice(0, 3).map((service, idx) => (
              <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                {typeof service === 'string' ? service : service.name}
              </span>
            ))}
            {shop.services.length > 3 && (
              <span className="text-xs text-gray-500 px-2 py-1">+{shop.services.length - 3}</span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 mt-auto pt-5">
          <Link 
            to={`/artisan/${shop.slug}`}
            className="flex-1 bg-[#1E3A5F] hover:bg-[#152a45] text-white text-center py-2.5 rounded-xl font-semibold text-sm transition-all"
          >
            Voir le profil
          </Link>
          <button
            onClick={handlePhoneClick}
            className="w-11 h-11 flex items-center justify-center bg-[#F97316] hover:bg-[#EA580C] text-white rounded-xl transition-colors"
          >
            <Phone size={18} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Artisans5EtoilesPage() {
  const [showFilters, setShowFilters] = useState(false);

  // Tous les artisans pour la recherche intelligente
  const allArtisans = MOCK_ARTISANS;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="artisans-5-etoiles-page">
      {/* Header avec barre de recherche intelligente Artisans */}
      <UnifiedHeader 
        onOpenFilters={() => setShowFilters(true)}
        artisansMode={true}
        artisansData={allArtisans}
      />
      
      {/* Navigation */}
      <MainNavigation onOpenFilters={() => setShowFilters(true)} />

      {/* Spacer Mobile */}
      <div className="h-11 md:hidden" />
      
      {/* Spacer Desktop/Tablet */}
      <div className="hidden md:block" style={{ height: '100px' }} />

      {/* Hero Banner - Doré/Jaune pour 5 étoiles */}
      <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 relative">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Award size={36} className="text-white" />
              <h1 className="text-3xl md:text-4xl font-black text-white">ARTISANS 5 ÉTOILES</h1>
              <Award size={36} className="text-white" />
            </div>
            <p className="text-white/90 text-lg mb-4">
              Les <span className="font-bold text-white">meilleurs artisans</span> notés par leurs clients
            </p>
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur rounded-full px-6 py-3 border-2 border-white/30">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={20} className="text-white fill-white" />
                ))}
              </div>
              <span className="text-white font-bold">Excellence garantie</span>
            </div>
          </div>
        </div>
      </div>

      {/* Badges de confiance */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-6 md:gap-10 flex-wrap">
            {[
              { icon: Star, text: 'Note 5/5 vérifiée', color: 'text-amber-500' },
              { icon: Shield, text: 'Artisans certifiés', color: 'text-emerald-500' },
              { icon: Award, text: 'Excellence reconnue', color: 'text-blue-500' },
              { icon: CheckCircle, text: 'Avis authentiques', color: 'text-purple-500' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center gap-2 text-gray-600">
                  <Icon size={18} className={item.color} />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Titre section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {ARTISANS_5_ETOILES.length} artisans d'exception
            </h2>
            <p className="text-gray-600">
              Découvre les artisans notés 5/5 par leurs clients sur AbracadaBati
            </p>
          </div>

          {/* Grille des artisans */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTISANS_5_ETOILES.map((artisan, index) => (
              <ShopCard key={artisan.id} shop={artisan} index={index} />
            ))}
          </div>

          {/* Message si aucun artisan */}
          {ARTISANS_5_ETOILES.length === 0 && (
            <div className="text-center py-16">
              <Star size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Aucun artisan 5 étoiles pour le moment
              </h3>
              <p className="text-gray-500">
                Les meilleurs artisans apparaîtront ici dès qu'ils auront obtenu la note maximale
              </p>
            </div>
          )}

          {/* Bannière CTA */}
          <div className="mt-12 bg-gradient-to-r from-[#1E3A5F] to-[#2563EB] rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Tu es artisan et tu veux apparaître ici ?</h3>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Rejoins AbracadaBati, offre un service d'excellence à tes clients et obtiens la note maximale pour figurer parmi les meilleurs !
            </p>
            <Link 
              to="/devenir-artisan"
              className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-8 py-3 rounded-xl transition-colors"
            >
              Devenir Artisan Partenaire
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </div>

      {/* Footer Global */}
      
    </div>
  );
}
